import React, { useState } from 'react';
import Button from '@atlaskit/button';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { InwardRelationships, OutwardRelationships } from '../../../../common/model/types.tsx';
import { SubtleText } from '../styled.tsx';
import PopupContent from './popup-content/index.tsx';

const messages = defineMessages({
	noDependencies: {
		id: 'project-pages-project-directories.components.view.compass-table.cell.no-dependencies',
		defaultMessage: 'No dependencies',
		description: 'Shown when a component has no dependencies.',
	},
	viewDependencies: {
		id: 'project-pages-project-directories.components.view.compass-table.cell.view-dependencies',
		defaultMessage: 'View dependencies',
		description: 'Button text for users to open a popup and view dependencies on a component.',
	},
});

type Props = {
	componentId: string;
	// Depended on by.
	inwardRelationships: InwardRelationships;

	// Depends on.
	outwardRelationships: OutwardRelationships;
};

export default function DependenciesCell(props: Props) {
	const { componentId, inwardRelationships, outwardRelationships } = props;
	const [isOpen, setIsOpen] = useState(false);

	const { formatMessage } = useIntl();

	if (inwardRelationships.nodes.length === 0 && outwardRelationships.nodes.length === 0) {
		return <SubtleText>{formatMessage(messages.noDependencies)}</SubtleText>;
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="left-start"
			messageId="project-directories.components.compass-table.cells.dependencies.popup"
			messageType="transactional"
			content={() => (
				<PopupContent
					testId="project-directories.components.compass-table.cells.dependencies.popup-wrapper"
					componentId={componentId}
					dependsOn={outwardRelationships}
					dependedOnBy={inwardRelationships}
				/>
			)}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					appearance="link"
					spacing="none"
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'viewDependenciesButton');
						setIsOpen(!isOpen);
					}}
				>
					{formatMessage(messages.viewDependencies)}
				</Button>
			)}
		/>
	);
}
